import { dataPreparationUiReducer } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-slice";
import { dataPreparationViewOptionsReducer } from "@/data-preparation-tool/store/data-preparation-view-options/data-preparation-view-options-slice";
import { registrationParametersReducer } from "@/data-preparation-tool/store/registration-parameters/registration-parameters-slice";
import { revisionReducer } from "@/data-preparation-tool/store/revision/revision-slice";
import { dataPreparationEditingStateReducer } from "@/data-preparation-tool/store/user-edits/data-preparation-user-edit-slice";
import { aiSliceReducer } from "@/features/ai-chat/ai-slice";
import { registrationReducer } from "@/registration-tools/common/store/registration-slice";
import { pdfReportReducer } from "@/tools/create-report/pdf-report-slice";
import {
  appReducer,
  captureTreeReducer,
  companyReducer,
  projectReducer as dataManagementProjectReducer,
  uiReducer as dataManagementUiReducer,
  userReducer as dataManagementUserReducer,
  fetchDataReducer,
  projectSettingsReducer,
  uploadTasksReducer,
} from "@faro-lotv/data-management";
import { iElementsReducer } from "@faro-lotv/project-source";
import {
  combineReducers,
  configureStore,
  ThunkAction,
  UnknownAction,
} from "@reduxjs/toolkit";
import { aiAssetReviewReducer } from "./ai-asset-review/ai-asset-review-slice";
import { backgroundTasksReducer } from "./background-tasks/background-tasks-slice";
import { cadReducer } from "./cad/cad-slice";
import { clippingBoxReducer } from "./clipping-box-slice";
import { createAnnotationReducer } from "./create-annotation-slice";
import { featuresReducer } from "./features/features-slice";
import { integrationsReducer } from "./integrations/integrations-slice";
import { lightweightElementsReducer } from "./lightweight-elements-slice";
import { locationMarkerReducer } from "./location-marker-slice";
import { measurementToolReducer } from "./measurement-tool-slice";
import { miniMapReducer } from "./minimap-slice";
import { changeMode, modeReducer } from "./mode-slice";
import { modesReducers } from "./modes";
import { pinMarkerReducer } from "./pin-marker-slice";
import { pointCloudAnalysisToolReducer } from "./point-cloud-analysis-tool-slice";
import { projectFilteringReducer } from "./project-filtering/project-filtering-slice";
import { projectReducer } from "./project-slice";
import { revisionsReducer } from "./revisions-slice";
import { selectionsReducer, setActiveElement } from "./selections-slice";
import { subscriptionFeaturesReducer } from "./subscription-features/subscription-features-slice";
import { tagsReducer } from "./tags/tags-slice";
import { uiReducer } from "./ui/ui-slice";
import { userReducer } from "./user-slice";
import { viewOptionsReducer } from "./view-options/view-options-slice";

const actionCreators = {
  setActiveElement,
  changeMode,
};

export const rootReducer = combineReducers({
  aiAssetReview: aiAssetReviewReducer,
  backgroundTasks: backgroundTasksReducer,
  features: featuresReducer,
  revisions: revisionsReducer,
  iElements: iElementsReducer,
  measurementTool: measurementToolReducer,
  miniMap: miniMapReducer,
  mode: modeReducer,
  selections: selectionsReducer,
  cad: cadReducer,
  ui: uiReducer,
  user: userReducer,
  subscriptionFeatures: subscriptionFeaturesReducer,
  integrations: integrationsReducer,
  project: projectReducer,
  createAnnotation: createAnnotationReducer,
  clippingBox: clippingBoxReducer,
  tags: tagsReducer,
  viewOptions: viewOptionsReducer,
  pointCloudAnalysisTool: pointCloudAnalysisToolReducer,
  pdfReport: pdfReportReducer,
  pinMarker: pinMarkerReducer,
  projectFiltering: projectFilteringReducer,
  locationMarker: locationMarkerReducer,
  ai: aiSliceReducer,

  ...modesReducers,

  revision: revisionReducer,
  dataPreparationViewOptions: dataPreparationViewOptionsReducer,
  dataPreparationUi: dataPreparationUiReducer,
  dataPreparationEditingState: dataPreparationEditingStateReducer,

  // The reducer below should only be used from the manual alignment tool
  registration: registrationReducer,
  registrationParameters: registrationParametersReducer,
  lightweightElements: lightweightElementsReducer,

  // Just like the iElementsReducer from @faro-lotv/project-source above, the reducers from the
  // Data Management library need to be added here so that they're usable within the library.
  dataManagementCaptureTree: captureTreeReducer,
  dataManagementProject: dataManagementProjectReducer,
  dataManagementCompany: companyReducer,
  dataManagementProjectSettings: projectSettingsReducer,
  dataManagementUi: dataManagementUiReducer,
  dataManagementUploadTasks: uploadTasksReducer,
  dataManagementUser: dataManagementUserReducer,
  dataManagementApp: appReducer,
  dataManagementFetchData: fetchDataReducer,
});

export const store = configureStore({
  reducer: rootReducer,

  // Add selectors to dropdown in the dev tools
  devTools: {
    actionCreators,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  undefined,
  UnknownAction
>;
